/**
 * Per-user localStorage cache of recently-selected pre-launch concepts
 * for the v2 Provisional Forecast Landing page.
 *
 * Why localStorage (and not the backend)?
 *   - Survives reloads with no API plumbing.
 *   - Per-browser is the right scope here — "recent" is a UX shortcut,
 *     not authoritative data, and the user's primary device is where
 *     it matters.
 *   - We key by user email so two users on the same machine don't see
 *     each other's history.
 *
 * Storage shape:
 *   key  = `provisionalForecastV2.recentConcepts:<email>`
 *   value = JSON.stringify(RecentConceptEntry[])
 *
 * Cap is 10 entries; the UI shows top 5 by default with a "view more"
 * to expand to all 10. Re-selecting an existing concept bumps it to
 * the front instead of adding a duplicate.
 */

import { PreLaunchConceptSuggestion } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';

export interface RecentConceptEntry extends PreLaunchConceptSuggestion {
  /** ISO timestamp; used only as a stable secondary sort and for telemetry. */
  lastUsedAt: string;
}

const KEY_PREFIX = 'provisionalForecastV2.recentConcepts';
export const MAX_RECENT_CONCEPTS = 10;

const buildKey = (userEmail: string | null | undefined): string | null => {
  // Anonymous (pre-auth) callers don't get a recent list — anything
  // we'd write would be globally visible to the next user on the
  // same browser. Returning null tells callers to skip persistence.
  if (!userEmail) return null;
  return `${KEY_PREFIX}:${userEmail.toLowerCase()}`;
};

const safeParse = (raw: string | null): RecentConceptEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentConceptEntry =>
        e &&
        typeof e === 'object' &&
        typeof (e as RecentConceptEntry).GFC === 'number' &&
        typeof (e as RecentConceptEntry).Concept === 'string' &&
        typeof (e as RecentConceptEntry).lastUsedAt === 'string'
    );
  } catch {
    return [];
  }
};

/**
 * Read the user's recent-concept list. Empty array on any failure
 * (missing user email, missing localStorage entry, malformed JSON).
 */
export const getRecentConcepts = (
  userEmail: string | null | undefined
): RecentConceptEntry[] => {
  const key = buildKey(userEmail);
  if (!key) return [];
  try {
    return safeParse(localStorage.getItem(key));
  } catch {
    // localStorage can throw (private mode, quota exceeded, etc.) —
    // never let that crash the search dropdown.
    return [];
  }
};

/**
 * Push a freshly-selected concept onto the user's recent list and
 * persist. Returns the updated list (caller is responsible for
 * pushing it into the MobX observable).
 *
 * Behaviour:
 *   - If the concept's GFC is already in the list, the existing
 *     entry is removed before we prepend the new one (so the same
 *     GFC never appears twice).
 *   - The list is capped at MAX_RECENT_CONCEPTS; the oldest entries
 *     fall off the tail.
 */
export const pushRecentConcept = (
  userEmail: string | null | undefined,
  concept: PreLaunchConceptSuggestion
): RecentConceptEntry[] => {
  const key = buildKey(userEmail);
  const existing = getRecentConcepts(userEmail);
  const filtered = existing.filter(e => e.GFC !== concept.GFC);
  const updated: RecentConceptEntry[] = [
    {
      GFC: concept.GFC,
      Concept: concept.Concept,
      lastUsedAt: new Date().toISOString(),
    },
    ...filtered,
  ].slice(0, MAX_RECENT_CONCEPTS);

  if (key) {
    try {
      localStorage.setItem(key, JSON.stringify(updated));
    } catch {
      // Quota / privacy mode — return the in-memory updated list
      // anyway so the caller's observable still reflects the change
      // for the current session.
    }
  }
  return updated;
};

/** Wipe the user's recent list. Returns the empty array for caller convenience. */
export const clearRecentConcepts = (
  userEmail: string | null | undefined
): RecentConceptEntry[] => {
  const key = buildKey(userEmail);
  if (key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore — we still return [] below.
    }
  }
  return [];
};
